import React, { useState, useEffect, useCallback } from 'react';
import './AdminMyClass.css';
import { fetchWithAuth } from '../utils/auth';
import { API_URL } from '../config';
import RichTextEditor from './RichTextEditor';

const AdminMyClass = () => {
  const [classes, setClasses] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState('');

  const [includeSecondary, setIncludeSecondary] = useState(false);
  const [ccSubs, setCcSubs] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // {sent_count, failed_count, failures}

  const [error, setError] = useState('');

  useEffect(() => {
    fetchWithAuth(`${API_URL}/api/teacher/my-classes`)
      .then(r => r.json())
      .then(d => {
        const list = d.classes || [];
        setClasses(list);
        if (list.length === 1) setSelectedId(String(list[0].id));
      })
      .catch(() => setError('Failed to load your classes.'))
      .finally(() => setLoaded(true));
  }, []);

  const selectedClass = classes.find(c => String(c.id) === String(selectedId));

  useEffect(() => {
    setPreview(null);
    setResult(null);
  }, [selectedId, includeSecondary, ccSubs]);

  const handlePreview = useCallback(async () => {
    if (!selectedClass) return;
    setError(''); setPreviewing(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/api/teacher/my-classes/${selectedClass.id}/recipients?include_secondary_email=${includeSecondary}&cc_subs=${ccSubs}`
      );
      const data = await res.json();
      if (res.ok) setPreview(data);
      else setError(data.error || 'Failed to resolve recipients.');
    } catch { setError('Network error.'); }
    setPreviewing(false);
  }, [selectedClass, includeSecondary, ccSubs]);

  const handleSend = async () => {
    setError(''); setResult(null);
    if (!selectedClass) { setError('Select a class first.'); return; }
    if (!subject.trim() || !bodyHtml.trim()) { setError('Subject and body are required.'); return; }
    const totalCount = (preview?.count || 0) + (preview?.sub_emails?.length || 0);
    if (!preview || totalCount === 0) { setError('Preview recipients first, and make sure at least one recipient matches.'); return; }
    if (!window.confirm(`Send this email to ${totalCount} recipient(s)? This cannot be undone.`)) return;

    setSending(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/teacher/my-classes/${selectedClass.id}/send`, {
        method: 'POST',
        body: JSON.stringify({
          subject, body_html: bodyHtml,
          include_secondary_email: includeSecondary, cc_subs: ccSubs,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult(data);
        setSubject(''); setBodyHtml(''); setPreview(null);
      } else {
        setError(data.error || 'Failed to send.');
      }
    } catch { setError('Network error.'); }
    setSending(false);
  };

  if (!loaded) return <div className="myclass-container"><p className="muted">Loading…</p></div>;

  if (classes.length === 0) {
    return (
      <div className="myclass-container">
        <h2>My Class</h2>
        <p className="muted">
          You're not currently assigned as the main teacher for any class. If this seems wrong,
          check with your branch admin.
        </p>
      </div>
    );
  }

  return (
    <div className="myclass-container">
      <h2>My Class</h2>
      <p className="myclass-subtitle">Email your class's parents directly.</p>

      {error && <div className="error-banner">{error}</div>}

      {classes.length > 1 && (
        <div className="form-card">
          <div className="form-row">
            <label>Class</label>
            <select value={selectedId} onChange={e => setSelectedId(e.target.value)}>
              <option value="">Select a class…</option>
              {classes.map(c => (
                <option key={c.id} value={c.id}>{c.grade_name} — {c.section}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {selectedClass && (
        <>
          <div className="form-card">
            <h3>1. Choose recipients</h3>
            <p className="muted">{selectedClass.grade_name} — Section {selectedClass.section}</p>
            <div className="myclass-option-row">
              <label className="myclass-checkbox">
                <input type="checkbox" checked={includeSecondary}
                       onChange={e => setIncludeSecondary(e.target.checked)} />
                Also send to secondary email
              </label>
              <label className="myclass-checkbox">
                <input type="checkbox" checked={ccSubs}
                       onChange={e => setCcSubs(e.target.checked)} />
                Also copy the sub teacher(s) for this class
              </label>
            </div>
            <button className="btn-secondary" onClick={handlePreview} disabled={previewing}>
              {previewing ? 'Checking…' : 'Preview recipients'}
            </button>

            {preview && (
              <div className="recipient-preview">
                <strong>{preview.count} parent recipient{preview.count === 1 ? '' : 's'}</strong>
                {ccSubs && preview.sub_emails.length > 0 && (
                  <span> + {preview.sub_emails.length} sub teacher{preview.sub_emails.length === 1 ? '' : 's'}</span>
                )}
                {preview.count > 0 && (
                  <ul className="recipient-preview-list">
                    {preview.recipients.slice(0, 8).map(r => (
                      <li key={r.email}>{r.email} <span className="muted">({r.labels.join(', ')})</span></li>
                    ))}
                    {preview.recipients.length > 8 && <li className="muted">…and {preview.recipients.length - 8} more</li>}
                    {ccSubs && preview.sub_emails.map(e => (
                      <li key={e}>{e} <span className="muted">(sub teacher)</span></li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div className="form-card">
            <h3>2. Compose</h3>
            <div className="form-row">
              <label>Subject</label>
              <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
                     placeholder="e.g. This week's homework" />
            </div>
            <div className="form-row">
              <label>Body</label>
              <RichTextEditor value={bodyHtml} onChange={setBodyHtml} />
            </div>
            <button className="btn-primary" onClick={handleSend} disabled={sending}>
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>

          {result && (
            <div className="form-card">
              <h3>Send result</h3>
              <p>Sent: {result.sent_count} &nbsp; Failed: {result.failed_count}</p>
              {result.failures.length > 0 && (
                <ul className="myclass-failures">
                  {result.failures.map((f, i) => (
                    <li key={i}>{f.email} — <span className="muted">{f.error}</span></li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminMyClass;
